'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Store,
  Globe,
  CreditCard,
  Sliders,
  Bell,
  Users,
  History,
  ShieldCheck,
  FileSpreadsheet,
  LogOut
} from 'lucide-react';

interface SettingsSidebarProps {
  currentPath?: string;
}

export function SettingsSidebar({ currentPath }: SettingsSidebarProps) {
  const pathname = usePathname();
  const activePath = currentPath || pathname;

  const businessLinks = [
    { label: 'Negocio', href: '/dashboard/settings', icon: Store },
    { label: 'Tu página de reservas', href: '/dashboard/settings/booking-page', icon: Globe },
    { label: 'Cobros y medios de pago', href: '/dashboard/settings/payment-methods', icon: CreditCard },
    { label: 'Reglas de reserva', href: '/dashboard/settings/booking-rules', icon: Sliders },
    { label: 'Avisos y recordatorios', href: '/dashboard/settings/reminders', icon: Bell },
  ];

  const accessLinks = [
    { label: 'Usuarios y permisos', href: '/dashboard/settings/team', icon: Users },
    { label: 'Historial de cambios', href: '/dashboard/settings/history', icon: History },
    { label: 'Cuenta y plan', href: '/dashboard/settings/subscription', icon: ShieldCheck },
    { label: 'Importar histórico', href: '/dashboard/settings/import', icon: FileSpreadsheet },
  ];

  const handleLogout = async () => {
    if (confirm('¿Deseas cerrar sesión?')) {
      await fetch('/api/auth/logout', { method: 'POST' });
      window.location.href = '/login';
    }
  };

  return (
    <div className="bg-white border border-gray-200/90 rounded-2xl p-4 shadow-2xs space-y-4 font-[family-name:var(--font-plus-jakarta)]">
      {/* TU NEGOCIO */}
      <div>
        <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-3 block mb-1.5 select-none">
          Tu Negocio
        </span>
        <div className="space-y-1">
          {businessLinks.map((item) => {
            const Icon = item.icon;
            const isActive = activePath === item.href;
            return (
              <Link
                key={item.label}
                href={item.href}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm transition-all ${
                  isActive
                    ? 'bg-[#E6F6F4] text-[#005C52] font-bold border border-[#00A896]/30 shadow-2xs'
                    : 'text-gray-600 hover:bg-[#F0FAF9] hover:text-[#005C52] font-medium'
                }`}
              >
                <Icon className={`w-4.5 h-4.5 shrink-0 ${isActive ? 'text-[#00A896]' : 'text-gray-400'}`} />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* ACCESOS */}
      <div className="pt-3 border-t border-gray-100">
        <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-3 block mb-1.5 select-none">
          Accesos
        </span>
        <div className="space-y-1">
          {accessLinks.map((item) => {
            const Icon = item.icon;
            const isActive = activePath === item.href;
            return (
              <Link
                key={item.label}
                href={item.href}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm transition-colors ${
                  isActive
                    ? 'bg-[#E6F6F4] text-[#005C52] font-bold border border-[#00A896]/30 shadow-2xs'
                    : 'text-gray-600 hover:bg-[#F0FAF9] hover:text-[#005C52] font-medium'
                }`}
              >
                <Icon className={`w-4.5 h-4.5 shrink-0 ${isActive ? 'text-[#00A896]' : 'text-gray-400'}`} />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Cerrar Sesión */}
      <div className="pt-2 border-t border-gray-100">
        <button
          onClick={handleLogout}
          type="button"
          className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50 rounded-xl transition-colors cursor-pointer"
        >
          <LogOut className="w-4.5 h-4.5 shrink-0" />
          <span>Cerrar sesión</span>
        </button>
      </div>
    </div>
  );
}
